import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { CalendarIcon, Loader2, AlertCircle } from "lucide-react";
import { DayPicker } from "react-day-picker";
import * as Dialog from "@radix-ui/react-dialog";
import * as Popover from "@radix-ui/react-popover";
import { cn } from "@/utils/stylesUtils";
import { MODAL_MODES, restrictionSchema } from "../constants";
import "react-day-picker/dist/style.css";

export default function RestrictionModal({
  open,
  mode,
  onSave,
  onClose,
  registerFormReset,
  isCreating = false,
  error = null,
}) {
  const isEdit = mode === MODAL_MODES.EDIT;

  const {
    control,
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(restrictionSchema),
    defaultValues: { startDate: undefined, endDate: undefined, reason: "" },
  });

  useEffect(() => {
    registerFormReset(reset);
  }, [registerFormReset, reset]);

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 z-50" />
        <Dialog.Content
          className={cn(
            "fixed z-50 bg-white outline-none",
            "bottom-0 left-0 right-0 rounded-t-lg",
            "sm:bottom-auto sm:left-1/2 sm:top-1/2",
            "sm:-translate-x-1/2 sm:-translate-y-1/2",
            "sm:rounded sm:w-full sm:max-w-lg sm:mx-6",
            "border border-[#DDDDDD]",
          )}
        >
          <div className="px-4 pt-5 pb-4 sm:px-6 sm:pt-6">
            <div className="w-10 h-1 bg-gray-200 rounded mx-auto mb-5 sm:hidden" />

            <Dialog.Title className="text-[16px] font-bold text-gray-900">
              {isEdit
                ? "Edit Pause Window"
                : "Turn app OFF for the following dates"}
            </Dialog.Title>
            <Dialog.Description className="text-[16px] opacity-50 mt-0.5">
              {isEdit
                ? "Update the dates for this pause rule."
                : "Set the window during which students cannot access the app."}
            </Dialog.Description>

            {/* ── Server error banner ───────────────────────────────────── */}
            {error && (
              <div className="mt-4 flex items-start gap-2 border border-[#DDDDDD] rounded p-3 bg-red-50">
                <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                <p className="text-[16px] text-red-700">{error}</p>
              </div>
            )}

            <div className="mt-4 flex flex-col gap-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex flex-col gap-1">
                  <label className="text-[16px] font-bold">
                    Begin turning the app OFF
                  </label>
                  <Controller
                    control={control}
                    name="startDate"
                    render={({ field }) => (
                      <DatePickerField
                        value={field.value}
                        onChange={field.onChange}
                        hasError={!!errors.startDate}
                        placeholder="Pick a date"
                        disabled={(date) =>
                          date < new Date(new Date().setHours(0, 0, 0, 0))
                        }
                      />
                    )}
                  />
                  {errors.startDate && (
                    <p className="text-[14px] text-red-500">
                      {errors.startDate.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-[16px] font-bold">
                    Turn the app back ON
                  </label>
                  <Controller
                    control={control}
                    name="endDate"
                    render={({ field }) => (
                      <DatePickerField
                        value={field.value}
                        onChange={field.onChange}
                        hasError={!!errors.endDate}
                        placeholder="Pick a date"
                        disabled={(date) => {
                          const start = getValues("startDate");
                          const min =
                            start ?? new Date(new Date().setHours(0, 0, 0, 0));
                          return date < min;
                        }}
                      />
                    )}
                  />
                  {errors.endDate && (
                    <p className="text-[14px] text-red-500">
                      {errors.endDate.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <label htmlFor="reason-input" className="text-[16px] font-bold">
                  Reason
                </label>
                <textarea
                  id="reason-input"
                  rows={4}
                  placeholder="Why is access being restricted?"
                  {...register("reason")}
                  className="w-full border border-[#DDDDDD] rounded px-3 py-2 text-[16px] focus:outline-none focus:border-[#3286DA] focus:ring-1 focus:ring-[#3286DA] resize-none transition placeholder:text-gray-400"
                />
                {errors.reason && (
                  <p className="text-[14px] text-red-500">
                    {errors.reason.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="border-t border-[#DDDDDD] px-4 py-3 flex gap-2 sm:px-6 sm:py-4">
            <Dialog.Close asChild>
              <button
                onClick={onClose}
                className="flex-1 py-2 border border-[#DDDDDD] rounded text-[16px] font-bold hover:bg-gray-100 text-gray-700 transition"
              >
                Cancel
              </button>
            </Dialog.Close>
            <button
              onClick={handleSubmit(onSave)}
              disabled={isCreating}
              className={cn(
                "flex-1 py-2 rounded text-[16px] font-bold transition",
                "bg-[#3386D912] border border-[#3286DA] text-[#1a5fa8]",
                "hover:bg-[#3386D924]",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                "inline-flex items-center justify-center gap-1.5",
              )}
            >
              {isCreating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {isCreating ? "Saving…" : "Save"}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function DatePickerField({ value, onChange, hasError, placeholder, disabled }) {
  const [open, setOpen] = useState(false);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={cn(
            "w-full flex items-center justify-between gap-2 text-left",
            "border rounded px-3 py-2 text-[16px] transition bg-white",
            "focus:outline-none focus:ring-1",
            hasError
              ? "border-red-400 focus:border-red-500 focus:ring-red-500 text-red-900"
              : "border-[#DDDDDD] focus:border-[#3286DA] focus:ring-[#3286DA]",
            !value && "text-gray-400",
          )}
        >
          <span className="flex-1">
            {value ? format(value, "PPP") : placeholder}
          </span>
          <CalendarIcon className="w-4 h-4 text-gray-400 shrink-0" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={6}
          className="z-[9999] bg-white rounded border border-[#DDDDDD] shadow-lg p-0 outline-none"
        >
          <DayPicker
            mode="single"
            selected={value}
            onSelect={(date) => {
              onChange(date);
              setOpen(false);
            }}
            disabled={disabled}
            initialFocus
            className="p-3 pointer-events-auto"
          />
          <Popover.Arrow className="fill-white" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
